<template>
  <div class="nosto-grid container-no-padding">
    <nosto-filter
      :nosto-ready="nostoReady"
      :choices="choices"
      :sort-by-options="sortByOptions"
      :total="total"
      @choices-updated="choicesUpdated"
      @clear-choices="clearChoices"
      @facets-ready="updateChoicesOnLoad"
    />

    <template v-if="nostoReady">
      <div
        v-if="!products.length"
        class="nosto-filter__error-message"
        v-text="$string(`search.no.products`)"
      />

      <div
        class="nosto-grid__grid grid nosto-grid__grid--products"
      >
        <!-- Products -->
        <template
          v-for="(product, index) of products"
          :key="`product-card-${liquid.sectionId}-${product.id}-${index}`"
        >
          <div
            v-if="inGridContentBannerAvailable && (index === inGridContentBannerPosition - 1)"
            :class="inGridContentBannerWidth"
          >
            <in-grid-content-banner
              :banner-image="inGridContentBanner.bannerImage"
              :banner-link="inGridContentBanner.bannerLink"
              :banner-text="inGridContentBanner.bannerText"
              :banner-video="inGridContentBanner.bannerVideo"
              :banner-width="inGridContentBanner.bannerWidth"
            />
          </div>

          <div class="col xs3 l3">
            <product-card
              v-if="product.id"
              block-class="nosto-grid"
              delay-click
              :index="index"
              :product="product"
              small
              @view-product="handleViewProduct"
            />
          </div>
        </template>
      </div>
    </template>

    <!-- Loading -->
    <div
      v-else
      class="nosto-grid__grid grid nosto-grid__grid--products"
      :class="[$class('loading')]"
    >
      <div
        v-for="(index) of $variable('settings.pagination.products')"
        :key="`product-card-${index}`"
        class="col xs3 l3"
      >
        <product-card
          block-class="nosto-grid"
          :index="index"
          :product="false"
          small
        />
      </div>
    </div>

    <nosto-pagination
      class="nosto-grid__pagination"
      :nosto-ready="nostoReady"
      :default-link="linkWithoutPage"
      :collection-link="liquid.url"
      :limit="limit"
      :page="page"
      :products="products"
      :state="state"
      :total="total"
      @choices-updated="choicesUpdated"
    />

    <!-- API -->
    <nosto-api-call
      :choices="choices"
      :collection-id="liquid.id"
      :limit="limit"
      :page="page"
      :query="query"
      :type="pageType"
      :storage-key="storageKey"
      @nosto-prices-updated="nostoPricesUpdated"
      @nosto-updated="nostoUpdated"
    />

    <!-- nosto drawer -->
    <overlay
      key="nostoDrawer"
      block-class="nosto-drawer-overlay"
      direction="left"
      namespace="nostoDrawer"
      type="drawer"
    >
      <template #body="{ isActive }">
        <nosto-drawer
          v-if="getComponentVisible('nosto-drawer')"
          v-cloak
          :is-active="isActive"
          :sort-by-options="sortByOptions"
          :total-products="total"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Nosto grid (nosto-grid)
 * -----------------------------------------------------------------------------
 * Shared nosto functionality for collection and search pages.
 *
 * @param {Object} inGridContentBanner - In-grid content banner metafields.
 * @param {Object} liquid - Liquid data.
 * @param {Number} productsPerPage - Products per page based on in-grid content
 * banner width.
 * @param {Object} sortByOptions - Sort by options.
 * @param {String} storageKey - Session storage key.
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'

import NostoApiCall from '~global/nosto-api-call/nosto-api-call'
import NostoFilter from '~async/nosto-grid/nosto-filter'
import NostoPagination from '~async/nosto-grid/nosto-pagination'
import InGridContentBanner from '~async/in-grid-content-banner/in-grid-content-banner'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~async/product-card/product-card'

import { formatNostoProduct } from '~/helpers/format-nosto-api'
import { waitFor } from '~/helpers/general'

export default {
  name: 'NostoGrid',

  components: {
    NostoApiCall,
    NostoDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.nosto-drawer' */
        '~async/nosto-drawer/nosto-drawer'
      ),
    }),
    NostoFilter,
    NostoPagination,
    InGridContentBanner,
    Overlay,
    ProductCard,
  },

  props: {
    inGridContentBanner: {
      type: Object,
      default: () => ({}),
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        handle: '',
        image: '',
        title: '',
        url: '',
      }),
    },
    productsPerPage: {
      type: Number,
      default: 16,
    },
    seoTitle: {
      type: String,
      default: '',
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
    storageKey: {
      type: String,
      default: '',
    },
    filterFamily: {
      type: Array,
      default: () => [],
    },
    facetExclusions: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'nosto-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      nostoReady: false,
      choices: null,
      filter: [],
      forceReplace: false,
      inGridContentBannerPosition: 5,
      linkWithoutPage: location.href.replaceAll(/[&|?]page=\d+/g, ''),
      page: 1,
      products: [],
      query: null,
      response: {},
      state: 'loading',
      total: 0,
    }
  },

  computed: {

    /**
     * Compute drawer choices.
     * @returns {String}
     */
    drawerChoices() {
      return this.getChoices()
    },

    /**
     * Compute if in-grid-content-banner available.
     * @returns {Boolean}
     */
    inGridContentBannerAvailable() {
      const hasImage = this.inGridContentBanner.bannerImage?.src
      const hasVideo = this.inGridContentBanner.bannerVideo?.sources

      return (
        (hasImage || hasVideo) &&
        (!this.choices || !this.choices.includes('pf_'))
      )
    },

    /**
     * Compute in-grid-content-banner width.
     * @returns {String}
     */
    inGridContentBannerWidth() {
      let bannerClass = 'col xs3 l3'
      const bannerWidth = this.inGridContentBanner.bannerWidth || 1
      switch (bannerWidth) {
        case 2:
          bannerClass = 'col xs6 l6'
          break
        case 4:
          bannerClass = 'col xs6 l12'
          break
        default:
          bannerClass = 'col xs3 l3'
      }

      return bannerClass
    },

    /**
     * Compute pagination limit based on base.
     * @returns {Number}
     */
    limit() {
      if (cnvs.page.type === 'collection') {
        return this.productsPerPage
      }

      return cnvs.settings.pagination.products
    },

    /**
     * Compute type for API.
     * @Returns {String}
     */
    pageType() {
      return cnvs.page.type === 'collection' ? 'collection' : 'search'
    },
  },

  watch: {

    /**
     * Watch drawer choices to update URL.
     */
    drawerChoices(choices) {
      if (choices === this.choices) {
        return
      }

      this.choices = choices

      /**
       * Don't set loading state if being updated from storage.
       */
      if (sessionStorage.getItem(this.storageKey)) {
        return
      }

      this.updateWindowLocationSearch(this.choices)
      this.nostoReady = false
    },

    /**
     * Update meta title after change page.
     */
    page() {
      if (!this.seoTitle) {
        return
      }

      /**
       * Some characters like '&' were escaped, resulting in '&amp;' outputted
       * to the browser tab. Parse to 'unescape' them.
       */
      let title = new DOMParser().parseFromString(this.seoTitle, 'text/html').documentElement.textContent

      if (this.page > 1) {
        const pageString = this.$string('general.meta.page', {
          replace: { page: this.page },
        })
        title += ` - ${pageString}`
      }

      document.title = title
    },
  },

  mounted() {
    this.setEventListeners()
    this.getSearchParams()

    if (window.cnvs.page.type === 'search') {
      this.setSearchQuery()
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateChoices: 'main-collection/updateChoices',
      updateFilters: 'main-collection/updateFilters',
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getChoices: 'main-collection/getChoices',
      getFilters: 'main-collection/getFilters',
    }),

    /**
     * Set event listeners.
     */
    setEventListeners() {
      window.addEventListener('click', this.handleBodyClick)
      window.addEventListener('popstate', this.getSearchParams)
    },

    /**
     * Check window.location.search on load to see if filter choices exist
     * before making API call.
     * - Restore saved data and scroll if it exists and matches current URL.
     * - Otherwise remove it as it's no longer needed.
     */
    getSearchParams() {
      if (sessionStorage.getItem(this.storageKey)) {
        const storedData = JSON.parse(sessionStorage.getItem(this.storageKey))

        if (storedData.url === location.href) {
          this.updateDataFromStorage(storedData)
          return
        }

        sessionStorage.removeItem(this.storageKey)
      }

      if (!window.location.search.includes('?')) {
        this.choices = ''
        this.nostoReady = false
        this.updateChoices(this.choices)
        return
      }

      const urlString = window.location.search.split('?')[1]

      const choicesArray = urlString.split('&').filter((choice) => {
        const filterOptionId = choice.split('=')[0]

        if (
          filterOptionId.includes('pf_') ||
          filterOptionId.includes('page') ||
          (filterOptionId.includes('sort') && choice !== '')
        ) {
          return choice
        }

        return false
      })

      const formatted = choicesArray.map((choice) => `&${choice}`)
      this.choices = formatted.join('')
      this.nostoReady = false
      this.updateChoices(this.choices)
    },

    /**
     * If previous data retrieve it and use to restore products and position.
     * - Delete once finished.
     * - Update functions have to come first as updating this.choices triggers
     *   API call component watch which removes sessionStorage.
     * @param {Object} data - Stored data.
     */
    updateDataFromStorage(data) {
      this.updateFilters(data.vue.filter)
      this.updateChoices(data.vue.choices)

      Object.keys(data.vue).forEach((key) => {
        this[key] = data.vue[key]
      })

      scrollTo(0, data.scrollY)
      this.nostoReady = true
      this.state = 'ready'
    },

    /**
     * Update window location search and browser history after choices updated.
     * @param {String} choices - Updated user choices.
     */
    updateWindowLocationSearch(choices) {
      let urlParameters = ''

      if (cnvs.page.type === 'search') {
        urlParameters += `?q=${this.query}`
      } else if (cnvs.page.collection === 'vendors' && !cnvs.page.handle) {
        urlParameters += `?q=${cnvs.page.title}`
      } else if (choices) {
        urlParameters += '?'
      }

      if (choices) {
        urlParameters += `&${choices}`
      }

      const url = location.search
        ? location.href.replace(location.search, urlParameters)
        : `${location.href}${urlParameters}`

      this.linkWithoutPage = url.replaceAll(/[&|?]page=\d+/g, '')

      window.history.pushState({}, '', url)
    },

    /**
     * Update pagination.
     * @param {String} pageNumber - Page number from choices.
     */
    updatePagination(pageNumber) {
      this.page = parseInt(pageNumber)
    },

    /**
     * Update facets on load.
     */
    updateChoicesOnLoad() {
      const facetValues = [...document.querySelectorAll('[js-nosto-facet="value"]')]

      const choicesArray = this.choices.split('&').filter((choice) => {
        return choice !== ''
      })

      let pageNumber

      facetValues.forEach((element) => element.classList.remove(this.$class('active')))

      choicesArray.forEach((choice) => {
        const value = choice.split('=')[1]
        const decoded = decodeURI(value)

        const activeValue = facetValues.find((element) => {
          return element.id === decoded
        })

        if (choice.includes('page')) {
          pageNumber = value
        }

        if (!activeValue) {
          return
        }

        activeValue.classList.add(this.$class('active'))
      })

      this.updatePagination(pageNumber)
    },

    /**
     * Clear choices when 'clear all' button is clicked.
     */
    clearChoices() {
      this.choices = ''
      this.nostoReady = false
      this.updateChoices(this.choices)
      this.updateWindowLocationSearch(this.choices)
    },

    /**
     * Callback after user choices emitted from facets to filter.
     * @param {Object} response - Response object with choices update.
     */
    choicesUpdated(response) {
      const { action, forceReplace = false, loadMore = false, requestParam } = response

      const requestValue = decodeURI(requestParam.split('=')[1])
      let requestKey = decodeURI(requestParam.split('=')[0])
      if (requestKey.includes('&')) {
        requestKey = requestKey.slice(1)
      }

      const params = new URLSearchParams(this.choices)
      params.delete('page')

      if (action === 'add') {
        params.set(requestKey, requestValue)

        if (requestKey === 'sort' && requestValue === 'recommended') {
          params.delete('sort')
        }
      } else {
        params.delete(requestKey)
      }

      this.choices = params.toString().replaceAll('+', ' ')

      if (forceReplace) {
        this.forceReplace = true
      }

      if (!loadMore) {
        this.nostoReady = false
      }

      if (requestKey === 'page') {
        this.page = Number(requestValue)
      }

      this.state = 'loading'
      this.updateChoices(this.choices)
      this.updateWindowLocationSearch(this.choices)
    },

    /**
     * Callback after a successful API call.
     * @param {Object} data - Response object from the API call.
     */
    nostoUpdated(data) {
      this.response = data

      /**
       * Update type data.
       */
      const newData = data.hits.map((object) => {
        return formatNostoProduct(object)
      })

      if (this.forceReplace) {
        this.products = newData
        this.forceReplace = false
      } else {

        /**
         * Update data based on current tab.
         */
        switch (data.action) {
          case 'replace':
            this.products = newData
            break
          case 'append':
            this.products = [...this.products, ...newData]
            break
          case 'prepend':
            this.products = [...newData, ...this.products]
            break
        }
      }

      this.total = data.total

      /**
       * Filters.
       */
      this.filter = data.facets

      if (this.facetExclusions) {
        const lowerCaseFacetExclusions = this.facetExclusions.join(',').toLowerCase().trim().split(',')

        this.filter = data.facets
          .filter(facet => !lowerCaseFacetExclusions.includes(facet.name.toLowerCase().trim()))
      }

      if (this.filterFamily.length) {
        const lowerCaseFilterFamily = this.filterFamily.join(',').toLowerCase().trim().split(',')

        this.filter = data.facets
          .filter(facet => lowerCaseFilterFamily.includes(facet.name.toLowerCase().trim()))
      }

      this.setFilterSwatches()
      this.updateFilters(this.filter)

      /**
       * Misc data.
       */
      this.page = data.page || 1
      this.state = 'ready'
      this.nostoReady = true

      /**
       * Emit for main search header.
       */
      this.$nextTick().then(() => {
        this.$emit('nosto-updated', {
          query: this.query,
          totalResults: this.total,
        })
      })
    },

    /**
     * Callback from Shopify Markets price data is ready.
     * - Update existing products data with new prices by matching ID.
     * - If no localised product or prices then reveal price.
     * - Better to show an incorrect price than a loading state.
     * @param {Array} prices - Localised price data.
     */
    nostoPricesUpdated(prices) {
      if (!prices.length) {
        this.products = this.products.map((product) => {
          return {
            ...product,
            price: {
              ...product.price,
              loading: false,
            },
          }
        })

        return
      }

      this.products = this.products.map((product) => {
        const localisedProduct = prices.find((localProduct) => {
          return localProduct.id === Number(product.id)
        })

        if (!localisedProduct) {
          return {
            ...product,
            price: {
              ...product.price,
              loading: false,
            },
          }
        }

        return {
          ...product,
          price: localisedProduct.price,
          compareAtPrice: localisedProduct.compareAtPrice,
        }
      })
    },

    /**
     * Event handler to close any open filters on body click.
     * @param {Event} event - Click event.
     */
    handleBodyClick(event) {
      const facets = document.querySelectorAll('[js-nosto-facet="facet"]')
      const sortBy = document.querySelector('[js-nosto-filter="sortBy"]')
      let bodyClick = true

      facets.forEach((facet) => {
        if (!facet.contains(event.target)) {
          return
        }

        bodyClick = false
      })

      if (sortBy !== null && sortBy.contains(event.target)) {
        bodyClick = false
      }

      if (!bodyClick) {
        return
      }

      facets.forEach((facet) => {
        facet.classList.remove(this.$class('active'))
      })

      if (sortBy === null) {
        return
      }

      sortBy.classList.remove(this.$class('active'))
    },

    /**
     * Set swatches for use in nosto Filter.
     */
    setFilterSwatches() {
      const data = this.filter.find((option) => {
        return option.filterType === 'opt_colour'
      })

      if (!data) {
        return
      }

      const swatches = data.values.map((swatch) => {
        const key = swatch.key.toLowerCase()

        const formatted = {
          key,
          swatch: `color-${key}_12x12.png`,
        }

        return formatted
      })

      window.cnvs = window.cnvs || {}
      window.cnvs.filterSwatches = swatches
    },

    /**
     * Set search query from URL parameters.
     */
    setSearchQuery() {
      if (!window.location.search.includes('?')) {
        this.query = false
        return
      }

      const urlParams = window.location.search.split('?')[1].split('&')
      const queryParam = urlParams.find((param) => {
        return param.includes('q=')
      })

      if (!queryParam) {
        return
      }

      this.query = queryParam.split('=')[1]
    },

    /**
     * Handle view product click.
     * - Save collection data to session storage so it can be retrieved when
     *   using browser navigation to go back and restore scroll position.
     * @param {String} url - URL to navigate to when complete.
     * @param {Number} productId - Product id.
     */
    handleViewProduct(url, productId) {
      const data = {
        scrollY,
        url: location.href,
        vue: {
          choices: this.choices,
          filter: this.filter,
          page: this.page,
          products: this.products,
          total: this.total,
        },
      }

      sessionStorage.setItem(this.storageKey, JSON.stringify(data))

      const type = this.pageType === 'collection' ? 'category' : 'serp'
      this.recordSearchClick(type, { productId, url: `https:${url}` }, url)
    },

    /**
     * Handle product click events.
     * @param {String} type - Event type.
     * @param {Object} hit - Product id and url.
     */
    recordSearchClick(type, hit, url) {
      waitFor('nostojs', () => {
        nostojs(api => {
          api.recordSearchClick(type, hit)

          this.$nextTick().then(() => {
            location.href = url
          })
        })
      })
    },
  },
}
</script>

<style lang="scss">
@import './nosto-grid';
@import '~async/product-card/product-card';
</style>
